import React, { useMemo, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
import { getMemberPhotoStyle, formatMemberLocation, getTranslatedField } from '../utils/member-helpers';
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
export function OurPeopleStories() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, {
    once: true,
    margin: '-50px'
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  // Shuffle once per mount, then duplicate for seamless infinite loop
  const shuffled = useMemo(() => shuffleArray(MEMBER_PROFILES), []);
  const doubled = [...shuffled, ...shuffled];
  return <section id="our-stories" ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-id="element-3262">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-3263">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-haa-skyblue/5 rounded-full blur-3xl" data-id="element-3264" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 bg-haa-midblue/5 rounded-full blur-3xl" data-id="element-3265" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3266">
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex flex-col items-center" data-id="element-3267">
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12 max-w-3xl" data-id="element-3268">
            <h2 className="font-bebas text-5xl md:text-6xl text-haa-navy tracking-wider mb-4" data-id="element-3269">
              {isEs ? 'Nuestra Gente, Nuestras Historias' : 'Our People Our Stories'}
            </h2>
            <p className="font-nunito text-lg text-gray-600 mb-5" data-id="element-3270">
              {isEs ? 'Conoce a los hondureño-americanos que están generando un impacto en todo Estados Unidos, liderando en los negocios, el servicio público y la comunidad.' : 'Meet the Honduran-Americans making an impact across the United States, leading in business, public service, and community.'}
            </p>
            <motion.a href="#/members" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 bg-haa-navy text-white font-nunito font-bold text-sm px-7 py-3 rounded-full shadow-md hover:bg-haa-blue transition-colors" data-id="element-3271">
              {isEs ? 'Conoce a Nuestra Gente' : 'Meet Our Gente'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-3272" />
            </motion.a>
          </motion.div>

          {/* Auto-scrolling row */}
          <motion.div variants={itemVariants} className="w-full" data-id="element-3273">
            <div className="relative w-full overflow-hidden" style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)'
          }} data-id="element-3274">
              <div className="flex member-scroll" style={{
              width: 'max-content'
            }} onMouseEnter={e => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.animationPlayState = 'paused';
            }} onMouseLeave={e => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.animationPlayState = 'running';
            }} data-id="element-3275">
                {doubled.map((member, i) => {
                const role = getTranslatedField(member, 'role', isEs);
                const badge = getTranslatedField(member, 'badge', isEs);
                return <a key={`${member.slug}-${i}`} href={`#/member/${member.slug}`} className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col flex-shrink-0 mx-3" style={{
                  width: '300px',
                  minWidth: '300px'
                }} data-id="element-3276">
                      {/* Image Container */}
                      <div className="relative h-56 overflow-hidden bg-gray-100" data-id="element-3277">
                        <img src={member.photo} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={getMemberPhotoStyle(member.slug, 'card')} data-id="element-3278" />
                        <div className="absolute inset-0 bg-gradient-to-t from-haa-navy/80 via-haa-navy/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" data-id="element-3279" />

                        {/* Badge */}
                        <div className="absolute top-4 right-4" data-id="element-3280">
                          <span className="inline-block px-3 py-1 bg-haa-skyblue text-white font-bebas text-sm tracking-wider rounded-full shadow-sm" data-id="element-3281">
                            {badge}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5 flex flex-col flex-grow" data-id="element-3282">
                        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider mb-1 group-hover:text-haa-blue transition-colors" data-id="element-3283">
                          {member.name}
                        </h3>
                        <p className="font-nunito text-haa-midblue font-semibold text-xs uppercase tracking-wider mb-1" data-id="element-3284">
                          {role}
                        </p>
                        {member.company && <p className="font-nunito text-gray-500 text-xs mb-0.5" data-id="element-3285">
                            {member.company}
                          </p>}
                        <p className="font-nunito text-gray-400 text-xs mb-3" data-id="element-3286">
                          {formatMemberLocation(member.city, member.state)}
                        </p>

                        <div className="mt-auto pt-3 flex items-center text-haa-blue font-nunito font-bold text-sm group-hover:text-haa-skyblue transition-colors" data-id="element-3287">
                          {isEs ? 'Ver Perfil' : 'View Profile'}
                          <ArrowRightIcon className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-3288" />
                        </div>
                      </div>
                    </a>;
              })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}